/**
 * Diabolical Tees product page extras on top of Horizon (loaded on product templates only).
 * - Gallery progress line: a 2px line under the main image that follows the selected slide.
 * - Image zoom "In place" (Theme settings > Product page): clicking or tapping the main image
 *   zooms it 2x inside its frame; again zooms out. Intercepts the click at window capture so
 *   Horizon's zoom popup does not open. Works with mouse and touch.
 * - Size selection "Shopper must choose" (Theme settings > Product page): see the section below.
 * - Gallery caption (Theme settings > Product page): a small line of text under the gallery.
 * - Size guide link: a Horizon "Popup link" block placed directly after the variant picker is
 *   shown on the size label row instead (see the section below).
 * Does nothing extra when the settings are on their defaults.
 */

const config = (() => {
  try {
    return JSON.parse(document.getElementById('dt-product-config')?.textContent || '{}');
  } catch {
    return {};
  }
})();


/* ---------- Gallery progress line ---------- */

/** @param {Element} gallery */
function visibleSlides(gallery) {
  return [...gallery.querySelectorAll('slideshow-slide')].filter(
    (slide) => slide instanceof HTMLElement && !slide.hidden && getComputedStyle(slide).display !== 'none'
  );
}

/** Last selected slide per gallery, so re-adding the line after a morph keeps its position. */
const currentSlide = new WeakMap();

/** @param {Element} gallery @param {Element | null | undefined} slide */
function updateProgress(gallery, slide) {
  if (slide) currentSlide.set(gallery, slide);
  const fill = gallery.querySelector('.dt-gallery-progress > span');
  if (!(fill instanceof HTMLElement)) return;
  const slides = visibleSlides(gallery);
  const active = slide ?? currentSlide.get(gallery);
  const index = Math.max(0, active ? slides.indexOf(/** @type {HTMLElement} */ (active)) : 0);
  fill.style.width = `${100 / Math.max(1, slides.length)}%`;
  const direction = getComputedStyle(gallery).direction === 'rtl' ? -1 : 1;
  fill.style.transform = `translateX(${index * 100 * direction}%)`;
}

/** @param {Element} gallery */
function ensureProgress(gallery) {
  const container = gallery.querySelector('slideshow-container');
  if (!container || visibleSlides(gallery).length < 2) {
    gallery.querySelector('.dt-gallery-progress')?.remove();
    return;
  }
  if (!container.querySelector(':scope > .dt-gallery-progress')) {
    const bar = document.createElement('span');
    bar.className = 'dt-gallery-progress';
    bar.setAttribute('aria-hidden', 'true');
    bar.append(document.createElement('span'));
    container.append(bar);
  }
  updateProgress(gallery, null);
}

/**
 * Adds (or re-adds after a morph) the caption line under the gallery. With thumbnails below the
 * image the caption goes right after the thumbnail strip, inside the gallery: the strip can hang
 * below the gallery's own box, so a caption placed after the gallery would sit behind it.
 */
function ensureCaption(gallery) {
  const text = typeof config.caption === 'string' ? config.caption.trim() : '';
  if (!text || gallery.closest('dialog')) return;

  const controls = gallery.querySelector('slideshow-controls');
  const position = controls?.getAttribute('pagination-position');
  const anchor = controls && position !== 'left' && position !== 'right' ? controls : gallery;

  const existing = [...gallery.querySelectorAll('.dt-gallery-caption')];
  const after = gallery.nextElementSibling;
  if (after?.classList.contains('dt-gallery-caption')) existing.push(after);

  let caption = anchor.nextElementSibling;
  if (!caption?.classList.contains('dt-gallery-caption')) {
    caption = document.createElement('p');
    caption.className = 'dt-gallery-caption';
    anchor.after(caption);
  }
  if (caption.textContent !== text) caption.textContent = text;
  for (const stale of existing) if (stale !== caption) stale.remove();
}

function setupGalleries() {
  for (const gallery of document.querySelectorAll('main media-gallery')) {
    ensureProgress(gallery);
    ensureCaption(gallery);
    if (gallery instanceof HTMLElement && !gallery.dataset.dtProgress) {
      gallery.dataset.dtProgress = 'true';
      gallery.addEventListener('slideshow:select', (event) => {
        updateProgress(gallery, /** @type {any} */ (event).detail?.slide);
        resetZoom();
      });
    }
  }
}

/* Horizon morphs the gallery and variant picker on variant change; re-apply our extras after it. */
let queued = 0;
const observer = new MutationObserver(() => {
  cancelAnimationFrame(queued);
  queued = requestAnimationFrame(() => {
    setupGalleries();
    applySizeState();
    ensureSizeGuide();
  });
});

/* ---------- In-place zoom ----------
   Click or tap the main image to zoom 2x inside its frame; click or tap again to zoom out.
   The image stays zoomed until then, even when the pointer leaves it. With a mouse the zoomed
   image follows the pointer. On touch screens the tap point is the zoom centre and dragging pans
   the image (the gallery swipe is paused while zoomed).
   The zoomed state lives in the dt-zoomed class, not in a saved element, and the pointer
   listeners sit on window, so the toggle keeps working if Horizon replaces the gallery nodes. */

const ZOOMABLE = 'main media-gallery .product-media-container--image.product-media-container--zoomable';
const ZOOMED = 'main media-gallery .product-media-container.dt-zoomed';
const DRAG_SLOP = 8;

/** Touch drag in progress on a zoomed image. */
let drag = null;
/** True when the last touch gesture was a drag, so the click that follows it does not zoom out. */
let dragged = false;

const clampPercent = (value) => Math.min(100, Math.max(0, value));

/** @param {HTMLElement} container @param {number} x @param {number} y */
function setOrigin(container, x, y) {
  container.style.setProperty('--dt-zoom-x', `${clampPercent(x)}%`);
  container.style.setProperty('--dt-zoom-y', `${clampPercent(y)}%`);
}

/** @param {HTMLElement} container @param {PointerEvent | MouseEvent} event */
function originFromPoint(container, event) {
  const rect = container.getBoundingClientRect();
  return {
    x: clampPercent(((event.clientX - rect.left) / rect.width) * 100),
    y: clampPercent(((event.clientY - rect.top) / rect.height) * 100),
  };
}

/** @param {Event} event @returns {HTMLElement | null} */
function zoomedTarget(event) {
  const container = event.target instanceof Element ? event.target.closest(ZOOMED) : null;
  return container instanceof HTMLElement ? container : null;
}

/** Where the current press started, to tell a click from a drag. */
let pressStart = { x: 0, y: 0 };

/**
 * Horizon's gallery starts a mouse drag on mousedown and, after 1px of movement, captures the
 * pointer, which retargets the click to the slideshow and scrolls the slides. While an image is
 * zoomed that drag is switched off, so a slightly unsteady click still zooms out.
 * @param {MouseEvent} event
 */
function onMouseDown(event) {
  if (zoomedTarget(event)) event.stopPropagation();
}

/** @param {PointerEvent} event */
function onPointerDown(event) {
  pressStart = { x: event.clientX, y: event.clientY };
  dragged = false;
  drag = null;
  const container = zoomedTarget(event);
  if (!container || event.pointerType === 'mouse') return;
  drag = {
    container,
    id: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    originX: parseFloat(container.style.getPropertyValue('--dt-zoom-x')) || 50,
    originY: parseFloat(container.style.getPropertyValue('--dt-zoom-y')) || 50,
  };
}

/** @param {PointerEvent} event */
function onPointerMove(event) {
  if (event.pointerType === 'mouse') {
    const container = zoomedTarget(event);
    if (!container) return;
    const origin = originFromPoint(container, event);
    setOrigin(container, origin.x, origin.y);
    return;
  }
  if (!drag || drag.id !== event.pointerId) return;
  const dx = event.clientX - drag.startX;
  const dy = event.clientY - drag.startY;
  if (Math.abs(dx) > DRAG_SLOP || Math.abs(dy) > DRAG_SLOP) dragged = true;
  // At 2x, moving the zoom centre by n% of the frame shifts the image by n% the other way.
  const rect = drag.container.getBoundingClientRect();
  setOrigin(drag.container, drag.originX - (dx / rect.width) * 100, drag.originY - (dy / rect.height) * 100);
}

function onPointerEnd() {
  drag = null;
}

function resetZoom() {
  for (const container of document.querySelectorAll(ZOOMED)) container.classList.remove('dt-zoomed');
  drag = null;
}

/** @param {MouseEvent} event */
function onZoomClick(event) {
  if (!(event.target instanceof Element)) return;
  let container = event.target.closest(ZOOMABLE);
  if (!container && event.target.closest('main media-gallery')) {
    // The gallery captured the pointer during a click that moved a pixel or two, so the click
    // arrived on the slideshow. If it was not a real drag, use the image under the pointer.
    const moved = Math.hypot(event.clientX - pressStart.x, event.clientY - pressStart.y);
    if (moved < DRAG_SLOP) {
      container = document.elementFromPoint(event.clientX, event.clientY)?.closest(ZOOMABLE) ?? null;
    }
  }
  if (!(container instanceof HTMLElement) || container.closest('.dialog-zoomed-gallery')) return;

  // Stop Horizon's document-level (capture) click router from opening the zoom popup.
  event.stopPropagation();
  event.preventDefault();

  const wasDrag = dragged;
  dragged = false;

  if (container.classList.contains('dt-zoomed')) {
    if (!wasDrag) resetZoom();
    return;
  }
  resetZoom();
  const origin = originFromPoint(container, event);
  setOrigin(container, origin.x, origin.y);
  container.classList.add('dt-zoomed');
}

/* ---------- Size selection: "Shopper must choose" ----------
   Horizon always preselects a variant. In this mode the size buttons are shown as unselected
   (Horizon's own selection stays intact, so colour changes keep working), the add to cart buttons
   read "Select a size", and add to cart is blocked (with a shake) until the shopper picks a size.
   A link with ?variant= counts as already chosen. This file covers the main product form; the
   quick add popup is handled by dt-quick-add-size.js. */

const SIZE_NAME = /size/i;
const SELECT_SIZE_LABEL = 'Select a size';
const CHOOSE_SIZE_LABEL = 'Choose a size';
const SIZE_NOTE = 'Please choose a size.';

let needsSize = config.size === 'require' && !new URLSearchParams(window.location.search).has('variant');

/** Size fieldsets (button-style variant picker) in the main product section. */
function sizeFieldsets() {
  return [...document.querySelectorAll('main .product-information fieldset.variant-option')].filter((fieldset) => {
    const input = fieldset.querySelector('input[type="radio"][data-option-name]');
    return input instanceof HTMLInputElement && SIZE_NAME.test(input.dataset.optionName || '');
  });
}

function addToCartButtons() {
  return [
    ...document.querySelectorAll('main .product-information button[name="add"], .sticky-add-to-cart__button'),
  ].filter((button) => button instanceof HTMLButtonElement);
}

/** Swaps the button's own text (Horizon renders the label as a bare text node next to icons). */
function setButtonLabel(button, label) {
  const node = [...button.childNodes].find((child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim());
  const target = node ?? button.querySelector('.add-to-cart-text__content');
  if (!target) return;
  if (!button.dataset.dtOriginalLabel) button.dataset.dtOriginalLabel = target.textContent.trim();
  if (target.textContent.trim() !== label) target.textContent = label;
}

function applySizeState() {
  const fieldsets = sizeFieldsets();
  if (!needsSize || fieldsets.length === 0) return;

  for (const fieldset of fieldsets) {
    fieldset.classList.add('dt-size-required');
    const value = fieldset.querySelector('legend .variant-option__swatch-value');
    if (value && value.textContent.trim() !== CHOOSE_SIZE_LABEL) value.textContent = CHOOSE_SIZE_LABEL;
    if (value instanceof HTMLElement) value.classList.add('dt-size-placeholder');
  }
  for (const button of addToCartButtons()) {
    if (!button.disabled) setButtonLabel(button, SELECT_SIZE_LABEL);
  }
}

function clearSizeState() {
  needsSize = false;
  for (const fieldset of document.querySelectorAll('main .dt-size-required')) {
    fieldset.classList.remove('dt-size-required', 'dt-shake');
    fieldset.querySelector('.dt-size-note')?.remove();
    fieldset.querySelector('.dt-size-placeholder')?.classList.remove('dt-size-placeholder');
  }
  for (const button of addToCartButtons()) {
    if (button.dataset.dtOriginalLabel) setButtonLabel(button, button.dataset.dtOriginalLabel);
  }
}

function nudgeSize() {
  const fieldset = sizeFieldsets()[0];
  if (!fieldset) return;

  if (!fieldset.querySelector('.dt-size-note')) {
    const note = document.createElement('p');
    note.className = 'dt-size-note';
    note.setAttribute('role', 'alert');
    note.textContent = SIZE_NOTE;
    fieldset.append(note);
  }

  fieldset.classList.remove('dt-shake');
  void fieldset.offsetWidth; // restart the animation
  fieldset.classList.add('dt-shake');
  fieldset.addEventListener('animationend', () => fieldset.classList.remove('dt-shake'), { once: true });

  const rect = fieldset.getBoundingClientRect();
  if (rect.top < 80 || rect.bottom > window.innerHeight) {
    fieldset.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }
  const first = fieldset.querySelector('input[type="radio"]:not(:disabled)');
  if (first instanceof HTMLElement) first.focus({ preventScroll: true });
}

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** @param {Event} event */
function blockWithoutSize(event) {
  if (!needsSize || !(event.target instanceof Element)) return;
  const isAddClick =
    event.type === 'click' &&
    event.target.closest('main .product-information button[name="add"], .sticky-add-to-cart__button');
  const isFormSubmit =
    event.type === 'submit' && event.target.closest('main .product-information') && event.target.matches('form');
  if (!isAddClick && !isFormSubmit) return;
  if (sizeFieldsets().length === 0) return;

  event.preventDefault();
  event.stopPropagation();
  nudgeSize();
}

/**
 * A click on any size (even the one Horizon preselected, which fires no change event) or a
 * keyboard change counts as the shopper choosing.
 * @param {Event} event
 */
function onSizePick(event) {
  if (!needsSize || !(event.target instanceof Element)) return;
  const picked =
    event.type === 'click'
      ? event.target.closest('main .dt-size-required .variant-option__button-label')
      : event.target.closest('main .dt-size-required') && event.target.matches('input[type="radio"]');
  if (picked) clearSizeState();
}

function setupSizeSelection() {
  if (!needsSize) return;
  applySizeState();
  window.addEventListener('click', blockWithoutSize, { capture: true });
  window.addEventListener('submit', blockWithoutSize, { capture: true });
  document.addEventListener('click', onSizePick);
  document.addEventListener('change', onSizePick);
}

/* ---------- Size guide link on the size row ----------
   The merchant adds Horizon's "Popup link" block directly after the variant picker. The popup
   itself stays where Horizon rendered it (the variant picker is re-rendered on every variant
   change, so nothing can live inside it); a small button in the size legend opens it instead and
   the original link is hidden. Without this script the original link simply shows under the picker. */

/** The popup link block that directly follows the variant picker, if any. */
function sizeGuidePopup() {
  const details = document.querySelector('main .product-information .product-details');
  let node = details?.querySelector('variant-picker');
  while (node && node !== details) {
    // The Popup link block prints its script tag (and sometimes styles) before the element itself.
    let next = node.nextElementSibling;
    while (next && next.matches('script, style, link')) next = next.nextElementSibling;
    if (next instanceof HTMLElement && next.matches('dialog-component.popup-link')) return next;
    node = node.parentElement;
  }
  return null;
}

function ensureSizeGuide() {
  const popup = sizeGuidePopup();
  const legend = sizeFieldsets()[0]?.querySelector('legend');
  const opener = popup?.querySelector('.popup-link__button');
  if (!popup || !(opener instanceof HTMLElement)) return;

  popup.classList.toggle('dt-size-guide-source', Boolean(legend));
  if (!legend || legend.querySelector('.dt-size-guide-link')) return;

  const link = document.createElement('button');
  link.type = 'button';
  link.className = 'dt-size-guide-link';
  link.textContent = opener.textContent.trim();
  legend.classList.add('dt-size-legend');
  legend.append(link);
}

/** @param {MouseEvent} event */
function onSizeGuideClick(event) {
  if (!(event.target instanceof Element) || !event.target.closest('.dt-size-guide-link')) return;
  event.preventDefault();
  const opener = sizeGuidePopup()?.querySelector('.popup-link__button');
  if (opener instanceof HTMLElement) opener.click();
}

/* ---------- Start ---------- */

function start() {
  setupGalleries();
  setupSizeSelection();
  ensureSizeGuide();
  document.addEventListener('click', onSizeGuideClick);
  const productSection = document.querySelector('main .product-information');
  // characterData: Horizon may rewrite button/label text in place after a variant change.
  const watch = { childList: true, subtree: true, characterData: needsSize };
  if (productSection) observer.observe(productSection, watch);
  const stickyBar = document.querySelector('sticky-add-to-cart');
  if (stickyBar) observer.observe(stickyBar, watch);

  if (config.zoom === 'inplace') {
    document.documentElement.classList.add('dt-zoom-inplace');
    window.addEventListener('click', onZoomClick, { capture: true });
    window.addEventListener('pointerdown', onPointerDown, { capture: true });
    window.addEventListener('mousedown', onMouseDown, { capture: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerEnd);
    window.addEventListener('pointercancel', onPointerEnd);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
